import { zodResolver } from '@hookform/resolvers/zod';
import axios from 'axios';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { MdOutlineImage } from 'react-icons/md';
import { z } from 'zod';

import { getServiceId } from '@/api/tokens.helper.ts';
import { imageVariantLink } from '@/common/media';
import { DateTimePicker, Modal, Toggle } from '@/components';
import { Button } from '@/components/Button';
import { Input, LOGO_PICKER_HEIGHT, LOGO_PICKER_WIDTH, LogoPicker } from '@/components/Inputs';
import formStyles from '@/components/Inputs/inputs.module.scss';
import { TagPicker } from '@/components/TagPicker/TagPicker';
import { useAuth } from '@/store/auth/auth.store';
import {
  type EventParticipant,
  EventParticipantType,
  type EventResponse,
  EVENTS,
  type SubEventInput,
  UNSPECIFIED_LOCATION,
} from '@/store/events/events.dto';
import { eventTags } from '@/store/events/events.mappers';
import { useEvents } from '@/store/events/events.store';
import {
  pendingGuestInvites,
  useDeleteEventInviteAnswerMutation,
  useEventInviteAnswersQuery,
  useEventInvitesQuery,
  useGuestInviteActions,
} from '@/store/invites/invites.store';
import { useAwayLabels } from '@/store/leaves/leaves.store';
import type { Person } from '@/store/persons/person.dto';
import { mapPeopleForSelection } from '@/store/persons/person.mapper';
import { usePerson } from '@/store/persons/person.store';
import { useTasks } from '@/store/tasks/tasks.store';
import type { ItineraryPrefill, ItineraryType } from '@/types/itineraryTypes.ts';
import { doDateRangesOverlap } from '@/utils/dateRangeUtils';
import { offsetDate, snapToNextSlot } from '@/utils/dateSlots';
import { parseAIError } from '@/utils/extractAI.ts';
import { roleToParticipantType, UserRole } from '@/utils/roles';
import { toast } from '@/utils/toast';
import TbTextScan2 from '@assets/icon-doc-scan.svg';
import ItinerarySuggestionsModal from '@components/CreateSheet/EditEventSheet/ItinerarySection/ItinerarySuggestionsModal.tsx';

import {
  CAROUSEL_SLIDE_HEIGHT,
  CAROUSEL_SLIDE_WIDTH,
  Carousel,
  type CarouselImage,
  getCarouselSlideSrc,
} from '../../ImageCarousel';
import OutOfWaterWarningModal from '../../OutOfWaterWarningModal/OutOfWaterWarningModal';
import { RichTextArea } from '../../RichTextArea/RichTextArea';
import styles from '../CreateSheet.module.scss';
import PeopleSelectionModal from '../PeopleSelectionModal';

import ItinerarySection, { type SubEvent } from './ItinerarySection/ItinerarySection';
import TasksSection, { type TaskItemType } from './TasksSection/TasksSection';

const createEventSchema = z
  .object({
    eventName: z.string().min(1, 'Please give the event a name.'),
    description: z.string().optional(),
    startDateTime: z.string(),
    endDateTime: z.string(),
    allDay: z.boolean(),
  })
  .refine(
    (data) => {
      if (!data.startDateTime || !data.endDateTime) return true;
      const start = new Date(data.startDateTime).getTime();
      const end = new Date(data.endDateTime).getTime();
      if (Number.isNaN(start) || Number.isNaN(end)) return true;
      return end >= start;
    },
    {
      message: 'The end time is before the start time.',
      path: ['endDateTime'],
    },
  );

type CreateEventFormValues = z.infer<typeof createEventSchema>;

export type CreateEventPayload = {
  id?: string;
  parent_id?: string;
  participants: EventParticipant[];
  /** Create only: owners or crew the backend should not seat. */
  excluded_participants?: string[];
  title: string;
  description: string;
  location: {
    name: string;
    address: string;
    latitude: number;
    longitude: number;
    map_url: string;
  };
  type: string;
  time_slot?: {
    type: string;
    start: string;
    end?: string;
    all_day?: boolean;
  };
  cover_image?: File | null;
  attachments?: File[];
  deleted_attachment_ids?: string[];
  sub_events?: SubEventInput[];
  tasks?: TaskItemType[];
  deleted_task_ids?: string[];
  /** Replaces the event's tags; left out, an update keeps them. `[]` clears them. */
  tag_ids?: string[];
};

type EditEventSheetProps = {
  open: boolean;
  onClose: () => void;
  onSubmitEvent?: (payload: CreateEventPayload) => void;
  title: string;
  initialData?: EventResponse;
  initialSubEvents?: SubEventInput[];
  initialEventType?: EVENTS;
  forceOutOfWater?: boolean;
};

const EditEventSheet = ({
  open,
  onClose,
  onSubmitEvent,
  title,
  initialData,
  initialSubEvents,
  initialEventType,
  forceOutOfWater = false,
}: EditEventSheetProps) => {
  const currentUserId = useAuth((state) => state.session?.id);
  const [selectedPeople, setSelectedPeople] = useState<EventParticipant[]>(() => {
    if (initialData?.participants && initialData.participants.length > 0) {
      return initialData.participants;
    }
    return [];
  });
  const [outOfWater, setOutOfWater] = useState(
    () => forceOutOfWater || initialData?.type === EVENTS.OUT_OF_WATER,
  );
  const { getTasksByEventId, getAllTasks } = useTasks();

  const [eventType, setEventType] = useState<EVENTS>(() =>
    initialData?.type && initialData.type !== EVENTS.OUT_OF_WATER
      ? initialData.type
      : (initialEventType ?? EVENTS.GENERAL),
  );
  const [showOutOfWaterWarning, setShowOutOfWaterWarning] = useState(false);
  const [pendingPayload, setPendingPayload] = useState<CreateEventPayload | null>(null);
  const prevOpenRef = useRef(false);
  // Ids of tasks loaded for an existing event — baseline for computing deletions on submit
  const [originalTaskIds, setOriginalTaskIds] = useState<string[]>([]);
  const logoInputRef = useRef<HTMLInputElement | null>(null);
  const [coverImage, setCoverImage] = useState<File | null>(null);
  const [isPickerActive, setIsPickerActive] = useState(() => !!initialData?.image);
  // A cover is on show: the event came with one, or one has gone through here. Cancel keeps it.
  const [hasCover, setHasCover] = useState(() => !!initialData?.image);
  const [attachmentsInputRef, setAttachmentsInputRef] = useState<HTMLInputElement | null>(null);
  const [attachments, setAttachments] = useState<CarouselImage[]>([]);
  const [subEvents, setSubEvents] = useState<SubEvent[]>(() =>
    [...(initialData?.sub_activities ?? initialSubEvents ?? [])].sort(
      (a, b) => new Date(a.time_slot.start).getTime() - new Date(b.time_slot.start).getTime(),
    ),
  );
  const [tasks, setTasks] = useState<TaskItemType[]>([]);
  // Sent only once the user edits the chips; otherwise an edit keeps the event's
  // tags and a new event is tagged by the AI after the save.
  const initialTags = useMemo(() => (initialData ? eventTags(initialData) : []), [initialData]);
  const [tagIds, setTagIds] = useState<string[]>(() => initialTags.map((tag) => tag.id));
  const [tagsTouched, setTagsTouched] = useState(false);

  const [questions, setQuestions] = useState<ItineraryType[] | null>(null);
  const [wizardPrefill, setWizardPrefill] = useState<ItineraryPrefill | null>(null);
  const [isWizardsShown, setIsWizardsShown] = useState<boolean>(false);

  const logoPickerRef = useCallback((input: HTMLInputElement | null) => {
    logoInputRef.current = input;
    if (input) {
      // The picker's input stays mounted, so this hears every pick, not only the first.
      input.addEventListener('change', () => setIsPickerActive(true));
    }
  }, []);

  const { people, getPeople } = usePerson();
  const { eventsList, uploadCoverImage } = useEvents();

  const mappedPeople = useMemo(() => mapPeopleForSelection(people), [people]);

  const currentUserPerson = useMemo(
    () => people?.find((p) => p.id === currentUserId) ?? null,
    [people, currentUserId],
  );

  const isOwner = (p: Person) => (p.services ?? []).some((pr) => pr.role === UserRole.OWNER);

  const pinnedPeople = useMemo(() => {
    const result: Person[] = [];
    const seen = new Set<string>();

    if (initialData) {
      // Editing: pin owner participants so they can't be removed
      (people ?? []).forEach((p) => {
        const isParticipant = selectedPeople.some((sp) => sp.person_id === p.id);
        if (isParticipant && isOwner(p) && !seen.has(p.id)) {
          result.push(p);
          seen.add(p.id);
        }
      });
      return result;
    }

    // Creating: the backend seats the creator and the whole crew, so they are shown
    // as already in and only guests are left to pick.
    if (currentUserPerson) {
      result.push(currentUserPerson);
      seen.add(currentUserPerson.id);
    }
    mappedPeople.forEach((mp) => {
      const person = people?.find((p) => p.id === mp.id);
      if (person && !seen.has(person.id) && mp.role !== UserRole.GUEST) {
        result.push(person);
        seen.add(person.id);
      }
    });
    return result;
  }, [initialData, currentUserPerson, people, mappedPeople, selectedPeople]);

  // Guests reached by email or phone are not vessel people to pick, so they show beside the
  // participants: those who answered without an account, and invitations still waiting.
  const { data: eventInvites } = useEventInvitesQuery(initialData?.id, initialData?.vessel_id);
  const { data: eventAnswers } = useEventInviteAnswersQuery(
    initialData?.id,
    initialData?.vessel_id,
  );
  const { mutateAsync: deleteAnswer } = useDeleteEventInviteAnswerMutation();
  const { cancel: cancelInvite } = useGuestInviteActions();
  // Taken off like a participant: hidden at once, but only removed when the event is saved.
  const [removedInvitees, setRemovedInvitees] = useState<string[]>([]);
  const allInvitees = useMemo(() => {
    const answers = eventAnswers ?? [];
    return [
      // An acceptance reads like any participant, with no status of its own. A decline is
      // left out, as a guest who declined is: the guests list in the event panel shows it.
      ...answers
        .filter((a) => a.accepted)
        .map((a) => ({
          id: a.id,
          name: a.name || a.contact,
          status: '',
          declined: false,
          extra: a.additionalGuests,
          remove: async () => {
            try {
              await deleteAnswer(a.id);
            } catch (error) {
              console.error('Remove answered guest error:', error);
              toast.error('Couldn’t remove the guest. Please try again.');
            }
          },
        })),
      ...pendingGuestInvites(
        eventInvites?.items,
        new Set(selectedPeople.map((p) => p.person_id)),
        answers,
      ).map((i) => ({
        id: i.id,
        name: i.name || i.contact,
        status: 'Invited',
        declined: false,
        extra: i.additionalGuests,
        remove: () => cancelInvite(i),
      })),
    ];
  }, [eventAnswers, eventInvites, selectedPeople, deleteAnswer, cancelInvite]);
  const invitees = allInvitees
    .filter((i) => !removedInvitees.includes(i.id))
    .map(({ id, name, status, declined, extra }) => ({
      id,
      name,
      status,
      declined,
      extra,
      onRemove: () => setRemovedInvitees((prev) => [...prev, id]),
    }));

  const defaultValues = useMemo<CreateEventFormValues>(() => {
    if (initialData) {
      return {
        eventName: initialData.title ?? '',
        description: initialData.description ?? '',
        startDateTime: initialData.time_slot?.start ?? '',
        endDateTime: initialData.time_slot?.end ?? '',
        allDay: initialData.time_slot?.all_day ?? false,
      };
    }
    const defaultStart = snapToNextSlot();
    const defaultEnd = offsetDate(defaultStart, 15);
    return {
      eventName: '',
      description: '',
      startDateTime: defaultStart.toISOString(),
      endDateTime: defaultEnd.toISOString(),
      allDay: false,
    };
  }, [initialData]);

  const {
    register,
    control,
    handleSubmit,
    setValue,
    setError,
    trigger,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<CreateEventFormValues>({
    resolver: zodResolver(createEventSchema),
    defaultValues,
    mode: 'onChange',
  });

  const startDateValue = useWatch({
    control,
    name: 'startDateTime',
  });

  const endDateValue = useWatch({
    control,
    name: 'endDateTime',
  });

  const allDayValue = useWatch({
    control,
    name: 'allDay',
  });

  // Who is on leave over the event's days: listed last in the participant picker, days beside them.
  const away = useAwayLabels(startDateValue, endDateValue);

  const setAllDayBounds = (startValue: string, endValue?: string) => {
    const start = new Date(startValue);
    start.setHours(0, 0, 0, 0);
    const end = new Date(endValue || startValue);
    end.setHours(23, 59, 0, 0);
    setValue('startDateTime', start.toISOString(), { shouldValidate: true });
    setValue('endDateTime', end.toISOString(), { shouldValidate: true });
  };

  const handleAllDayChange = (checked: boolean) => {
    setValue('allDay', checked, { shouldValidate: true });
    if (checked) {
      if (startDateValue) setAllDayBounds(startDateValue, endDateValue);
      return;
    }
    const day = startDateValue ? new Date(startDateValue) : new Date();
    const slot = snapToNextSlot();
    const start = new Date(day);
    start.setHours(slot.getHours(), slot.getMinutes(), 0, 0);
    setValue('startDateTime', start.toISOString(), { shouldValidate: true });
    const endDay = endDateValue ? new Date(endDateValue) : day;
    const end = new Date(endDay);
    end.setHours(slot.getHours(), slot.getMinutes(), 0, 0);
    const safeEnd = end.getTime() > start.getTime() ? end : offsetDate(start, 15);
    setValue('endDateTime', safeEnd.toISOString(), { shouldValidate: true });
  };

  const isStartInPast = useMemo(() => {
    if (!startDateValue) return false;
    return new Date(startDateValue) < new Date();
  }, [startDateValue]);

  // Revalidate end date when start date changes
  useEffect(() => {
    void trigger('endDateTime');
  }, [startDateValue, trigger]);

  const handleSelectPerson = (id: string) => {
    const person = mappedPeople.find((p) => p.id === id);
    const role = person?.role;
    const name = person ? `${person.firstName} ${person.lastName}` : 'Unknown person';
    const type = roleToParticipantType(role) as EventParticipantType;
    setSelectedPeople((prev) => [...prev, { person_id: id, type, name }]);
  };

  const handleRemovePerson = (id: string) => {
    setSelectedPeople((prev) => prev.filter((person) => person.person_id !== id));
  };

  const outOfWaterEvents = useMemo(() => {
    const events = eventsList?.items.filter((event) => event.type === EVENTS.OUT_OF_WATER) || [];
    // When editing, exclude the current event from overlap check
    if (initialData?.id) {
      return events.filter((event) => event.id !== initialData.id);
    }
    return events;
  }, [eventsList, initialData]);

  const checkOutOfWaterOverlap = (startDateTime: string, endDateTime: string): boolean => {
    const eventStart = new Date(startDateTime);
    const eventEnd = new Date(endDateTime);

    return outOfWaterEvents.some(
      (oowEvent) =>
        !!oowEvent.time_slot &&
        doDateRangesOverlap(eventStart, eventEnd, oowEvent.time_slot.start, oowEvent.time_slot.end),
    );
  };

  const clearAttachments = useCallback((imgs: CarouselImage[]) => {
    imgs.forEach((img) => {
      if (img.type === 'file') URL.revokeObjectURL(img.preview);
    });
    setAttachments([]);
  }, []);

  const submitEvent = (payload: CreateEventPayload) => {
    onSubmitEvent?.(payload);
    void Promise.all(
      allInvitees.filter((i) => removedInvitees.includes(i.id)).map((i) => i.remove()),
    );
    setRemovedInvitees([]);
    onClose();
    reset(defaultValues);
    setSelectedPeople([]);
    setOutOfWater(forceOutOfWater);
    setEventType(EVENTS.GENERAL);
    setCoverImage(null);
    setIsPickerActive(false);
    setPendingPayload(null);
    clearAttachments(attachments);
    setSubEvents([]);
    setTasks([]);
    setTagIds([]);
    setTagsTouched(false);
    void getAllTasks();

    if (navigator.onLine) {
      toast.success(initialData ? 'Your event has been updated.' : 'Your event has been added.');
    } else {
      toast.info('Your event will save once you’re back online.');
    }
  };

  const onSubmit = (rawValues: CreateEventFormValues) => {
    const values = { ...rawValues };
    if (values.allDay && values.startDateTime) {
      const start = new Date(values.startDateTime);
      start.setHours(0, 0, 0, 0);
      const end = new Date(values.endDateTime || values.startDateTime);
      end.setHours(23, 59, 0, 0);
      values.startDateTime = start.toISOString();
      values.endDateTime = end.toISOString();
    }
    // End is optional: an empty end is sent as no end, the way the wizard creates it.
    const hasDates = !!values.startDateTime;
    // An event without dates is an idea; a haul-out is not, so it keeps its dates.
    if (outOfWater && !hasDates) {
      setError('startDateTime', {
        message: values.allDay
          ? 'Please enter a start date.'
          : 'Please choose a start date and time.',
      });
      return;
    }
    // Overlap checks still need a range, so an open-ended event spans its start.
    const rangeEnd = values.endDateTime || values.startDateTime;

    let payload: CreateEventPayload;

    const attachmentFiles = attachments
      .filter((img): img is Extract<CarouselImage, { type: 'file' }> => img.type === 'file')
      .map((img) => img.file);

    const keptAttachmentIds = new Set(
      attachments
        .filter(
          (img): img is Extract<CarouselImage, { type: 'attachment' }> => img.type === 'attachment',
        )
        .map((img) => img.id),
    );
    const convertedSubEvents = subEvents.map((se) => ({
      ...se,
      time_slot: { start: se.time_slot.start, end: se.time_slot.end },
    }));

    const deletedAttachmentIds = (initialData?.attachments ?? [])
      .filter((a) => !keptAttachmentIds.has(a.id))
      .map((a) => a.id);

    const currentTaskIds = new Set(tasks.map((t) => t.id));
    const deletedTaskIds = originalTaskIds.filter((id) => !currentTaskIds.has(id));

    const timeSlot = hasDates
      ? {
          type: initialData?.time_slot?.type ?? 'day_based',
          start: values.startDateTime,
          end: values.endDateTime || undefined,
          all_day: values.allDay,
        }
      : undefined;

    // Where an event happens is edited in the wizard's Location step, not here, so
    // an existing record is handed back exactly as it came.
    const location = initialData?.location ?? {
      name: UNSPECIFIED_LOCATION,
      address: '',
      latitude: 0,
      longitude: 0,
      map_url: '',
    };

    if (initialData) {
      // Editing existing event - preserve all fields and update with form values
      payload = {
        id: initialData.id,
        parent_id: initialData.parent?.id,
        participants: selectedPeople,
        title: values.eventName,
        description: values.description?.trim() || '',
        location,
        type: outOfWater ? EVENTS.OUT_OF_WATER : eventType,
        time_slot: timeSlot,
        // The cover is already saved — it uploads on Done (see the LogoPicker below).
        attachments: attachmentFiles,
        deleted_attachment_ids: deletedAttachmentIds,
        sub_events: convertedSubEvents,
        tasks: tasks,
        deleted_task_ids: deletedTaskIds,
        ...(tagsTouched ? { tag_ids: tagIds } : {}),
      };
    } else {
      // Creating new event
      payload = {
        participants: selectedPeople,
        title: values.eventName,
        description: values.description?.trim() || '',
        location,
        type: outOfWater ? EVENTS.OUT_OF_WATER : eventType,
        time_slot: timeSlot,
        cover_image: coverImage,
        attachments: attachmentFiles,
        sub_events: convertedSubEvents,
        tasks: tasks,
        ...(tagsTouched ? { tag_ids: tagIds } : {}),
      };
    }

    // Only warn about overlap if:
    // 1. This is NOT an out-of-water event, AND
    // 2. Dates are set, AND
    // 3. Either creating new event OR time slot has changed from original
    const originalSlot = initialData?.time_slot;
    const timeSlotChanged =
      !initialData ||
      !originalSlot ||
      new Date(values.startDateTime).getTime() !== new Date(originalSlot.start).getTime() ||
      new Date(rangeEnd).getTime() !== new Date(originalSlot.end || originalSlot.start).getTime();

    if (
      hasDates &&
      !outOfWater &&
      timeSlotChanged &&
      checkOutOfWaterOverlap(values.startDateTime, rangeEnd)
    ) {
      setPendingPayload(payload);
      setShowOutOfWaterWarning(true);
      return;
    }

    submitEvent(payload);
  };

  const handleWarningClose = () => {
    setShowOutOfWaterWarning(false);
    setPendingPayload(null);
  };

  const handleChangeEvent = () => {
    setShowOutOfWaterWarning(false);
    setPendingPayload(null);
  };

  const handleGoOn = () => {
    if (pendingPayload) {
      submitEvent(pendingPayload);
    }
    setShowOutOfWaterWarning(false);
  };

  const handleClose = () => {
    onClose();
    reset(defaultValues);
    setSelectedPeople([]);
    setOutOfWater(forceOutOfWater);
    setEventType(EVENTS.GENERAL);
    setCoverImage(null);
    setIsPickerActive(false);
    clearAttachments(attachments);
    setSubEvents([]);
    setTasks([]);
  };

  useEffect(() => {
    // Only sync state when modal transitions from closed to open
    if (open && !prevOpenRef.current) {
      void getPeople();
      // Use queueMicrotask to avoid synchronous setState in effect warning
      queueMicrotask(() => {
        setRemovedInvitees([]);
        setOutOfWater(forceOutOfWater || initialData?.type === EVENTS.OUT_OF_WATER);
        setEventType(
          initialData?.type && initialData.type !== EVENTS.OUT_OF_WATER
            ? initialData.type
            : (initialEventType ?? EVENTS.GENERAL),
        );
        setSelectedPeople(
          initialData?.participants && initialData.participants.length > 0
            ? initialData.participants
            : [],
        );
        setIsPickerActive(!!initialData?.image);
        setHasCover(!!initialData?.image);
        setAttachments(
          (initialData?.attachments ?? []).map((a) => ({
            type: 'attachment' as const,
            id: a.id,
            src: a.link,
            thumb: imageVariantLink(a, CAROUSEL_SLIDE_WIDTH, CAROUSEL_SLIDE_HEIGHT) ?? undefined,
            name: a.name,
          })),
        );
        setSubEvents([...(initialData?.sub_activities ?? initialSubEvents ?? [])]);
        // Editing: load tasks already attached to this event so they show in the
        // section (not just ones added this session). New ones get temp- ids on add.
        if (initialData?.id) {
          void getTasksByEventId(initialData.id).then((loaded) => {
            setOriginalTaskIds(loaded.map((t) => t.id));
            setTasks(loaded as unknown as TaskItemType[]);
          });
        } else {
          setOriginalTaskIds([]);
          setTasks([]);
        }
        reset(defaultValues);
      });
    }
    prevOpenRef.current = open;
  }, [
    open,
    getPeople,
    getTasksByEventId,
    initialData,
    initialSubEvents,
    initialEventType,
    forceOutOfWater,
    defaultValues,
    reset,
  ]);

  const getItineraryWizards = async () => {
    try {
      const vesselId = getServiceId();
      const result = await axios.get(
        `${import.meta.env.VITE_APP_EXTRACT_URL}/api/ai/itinerary/wizard`,
        vesselId ? { params: { vessel_id: vesselId } } : undefined,
      );
      if (result.status === 200) {
        setIsWizardsShown(!!result.data);
        setQuestions(result.data.questions);
        setWizardPrefill(result.data.prefill ?? null);
      }
    } catch (error) {
      toast.error(parseAIError(error).message);
    }
  };

  return (
    <Modal open={open} onClose={handleClose} title={title}>
      <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
        {!coverImage && !isPickerActive && (
          <Button type="button" onClick={() => logoInputRef.current?.click()}>
            <MdOutlineImage />
            Add Cover Image
          </Button>
        )}
        <div style={isPickerActive || coverImage ? undefined : { display: 'none' }}>
          <LogoPicker
            /* An existing event's cover saves on Done; a new one rides with the create. */
            onChange={(img) =>
              initialData ? uploadCoverImage(initialData.id, img) : setCoverImage(img)
            }
            onSave={() => setHasCover(true)}
            // Cancel leaves a cover that is already there on show.
            onCancel={() => setIsPickerActive(hasCover)}
            initialLogo={imageVariantLink(
              initialData?.image,
              LOGO_PICKER_WIDTH,
              LOGO_PICKER_HEIGHT,
            )}
            round={false}
            ref={logoPickerRef}
          />
        </div>
        <Input
          label="Event Name"
          placeholder="Owner’s birthday dinner"
          mandatory
          error={errors.eventName?.message}
          {...register('eventName')}
        />

        {outOfWater && (
          <div className={formStyles.InputRoot}>
            <div className={formStyles.Label}>
              <span>Event Type</span>
            </div>
            <div className={clsx(formStyles.InputWrapper, formStyles.disabled)}>
              <div className="body-medium">Out of Water</div>
            </div>
          </div>
        )}

        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <RichTextArea
              label="Description"
              placeholder="Description"
              value={field.value ?? ''}
              onChange={field.onChange}
              error={errors.description?.message}
            />
          )}
        />
        <DateTimePicker
          label="Dates"
          placeholder="Choose dates"
          mandatory={outOfWater}
          allDay={allDayValue}
          onAllDayChange={handleAllDayChange}
          value={startDateValue || null}
          endValue={endDateValue || null}
          onRangeChange={(start, end) => {
            setValue('startDateTime', start ?? '', { shouldValidate: true });
            setValue('endDateTime', end ?? '', { shouldValidate: true });
          }}
          error={errors.endDateTime?.message ?? errors.startDateTime?.message}
          warning={
            isStartInPast ? 'This event is in the past. Please check the start date.' : undefined
          }
        />

        <PeopleSelectionModal
          people={people?.filter((p) => !pinnedPeople.some((pp) => pp.id === p.id)) || []}
          selectedIds={[
            ...selectedPeople
              .filter((p) => !pinnedPeople.some((pp) => pp.id === p.person_id))
              .map((p) => p.person_id),
            ...pinnedPeople.map((p) => p.id),
          ]}
          onSelect={handleSelectPerson}
          onRemove={handleRemovePerson}
          triggerLabel="Add Participants"
          modalTitle="Add Participants"
          emptyMessage="No people match. Please try a different search."
          label="Participants"
          pinnedPeople={pinnedPeople}
          invitees={invitees}
          showAddAll={!!initialData}
          addAllLabel="Add All Participants"
          away={away}
        />

        <p style={{ fontSize: 14 }}>Photos {!!attachments.length && `(${attachments.length})`}</p>

        {attachments.length > 0 && (
          <Carousel>
            <Carousel.Prev />
            <Carousel.Track>
              {attachments.map((img, i) => (
                <Carousel.Slide
                  key={i}
                  src={getCarouselSlideSrc(img)}
                  onDelete={() => {
                    if (img.type === 'file') URL.revokeObjectURL(img.preview);
                    setAttachments((prev) => prev.filter((_, idx) => idx !== i));
                  }}
                />
              ))}
            </Carousel.Track>
            <Carousel.Next />
          </Carousel>
        )}

        <input
          style={{ display: 'none' }}
          type="file"
          accept="image/*"
          multiple
          ref={setAttachmentsInputRef}
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            const newImages: CarouselImage[] = files.map((file) => ({
              type: 'file',
              file,
              preview: URL.createObjectURL(file),
            }));
            setAttachments((prev) => [...prev, ...newImages]);
            e.target.value = '';
          }}
        />
        <div
          style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', width: 'fit-content' }}
          onClick={() => attachmentsInputRef?.click()}
        >
          <div
            style={{
              width: 28,
              height: 28,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: 8,
              background: 'var(--color-white-30)',
              borderRadius: 4,
            }}
          >
            <MdOutlineImage />
          </div>
          Attach your photos
        </div>

        <div>
          <ItinerarySection subEvents={subEvents} onChange={setSubEvents} />
          <button
            type="button"
            className={clsx(
              styles.itineraryAiButton,
              !outOfWater ? styles.itineraryAiButtonVisible : styles.itineraryAiButtonHidden,
            )}
            onClick={getItineraryWizards}
            aria-hidden={outOfWater}
            tabIndex={outOfWater ? -1 : 0}
          >
            <p>You can add Itinerary info with AI</p>
            <img
              width={20}
              height={20}
              src={TbTextScan2}
              alt={'scan vessel item'}
              draggable={false}
            />
          </button>
        </div>

        <TasksSection tasks={tasks} onChange={setTasks} />

        {!outOfWater && (
          <div className={styles.tagsField}>
            <p className={styles.sectionTitle}>Tags</p>
            <TagPicker
              value={tagIds}
              known={initialTags}
              onChange={(next) => {
                setTagIds(next);
                setTagsTouched(true);
              }}
              emptyText={initialData ? 'No tags' : 'The AI adds tags after saving'}
            />
          </div>
        )}

        {!forceOutOfWater && (
          <Toggle
            labelOn="Out of water"
            labelOff="Not out of water"
            variant="warning"
            checked={outOfWater}
            onChange={(e) => setOutOfWater(e.target.checked)}
          />
        )}

        <Button type="submit" disabled={isSubmitting}>
          {initialData ? 'Save Event' : 'Add Event'}
        </Button>
      </form>

      <OutOfWaterWarningModal
        open={showOutOfWaterWarning}
        onClose={handleWarningClose}
        onChangeEvent={handleChangeEvent}
        onGoOn={handleGoOn}
      />

      <ItinerarySuggestionsModal
        key={isWizardsShown ? 'wizard-open' : 'wizard-closed'}
        title={'Wizards'}
        open={isWizardsShown}
        onClose={() => setIsWizardsShown(!isWizardsShown)}
        questions={questions}
        prefill={wizardPrefill}
      />
    </Modal>
  );
};

export default EditEventSheet;
export type { CreateEventFormValues };
